import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Link,
  Container,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { Email, ArrowBack } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useNotification } from '../contexts/NotificationContext';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const { showSuccess, showError } = useNotification();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      showError('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/auth/forgot-password`, { email });
      setSent(true);
      showSuccess('Reset instructions have been sent to your email');
    } catch (error: any) {
      showError(error.response?.data?.message || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 3,
          }}
        >
          <Card sx={{ width: '100%', maxWidth: 400, boxShadow: 3 }}>
            <CardContent sx={{ padding: 4, textAlign: 'center' }}>
              <Typography variant="h5" gutterBottom>
                Check Your Email
              </Typography>
              <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
                We've sent password reset instructions to:
              </Typography>
              <Typography variant="body1" fontWeight="bold" sx={{ mb: 3 }}>
                {email}
              </Typography>
              <Link component={RouterLink} to="/login" variant="body2">
                Back to Sign In
              </Link>
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 3,
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 400, boxShadow: 3 }}>
          <CardContent sx={{ padding: 4 }}>
            <Box sx={{ mb: 3 }}>
              <Link
                component={RouterLink}
                to="/login"
                sx={{ display: 'flex', alignItems: 'center', mb: 2 }}
              >
                <ArrowBack sx={{ mr: 1 }} />
                Back to Sign In
              </Link>
              <Typography variant="h5" gutterBottom>
                Reset Password
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Enter your email address and we'll send you instructions to reset your password.
              </Typography>
            </Box>

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                variant="outlined"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email />
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{ mt: 3, mb: 2, height: 48 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Send Reset Instructions'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
}